import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import {
  VIDEOS_LATERAIS_MAX,
  VIDEOS_MAX_ITENS,
  VIDEOS_TITULO,
} from './helpers';

const messages = defineMessages({
  titleBlock: { id: 'mpf_videos_block', defaultMessage: 'Vídeos' },
  titulo: { id: 'mpf_videos_titulo', defaultMessage: 'Título da faixa' },
  tituloHelp: {
    id: 'mpf_videos_titulo_help',
    defaultMessage: 'Em branco, usa "{padrao}".',
  },
  exibirTitulo: {
    id: 'mpf_videos_exibir_titulo',
    defaultMessage: 'Exibir o título',
  },
  exibirTituloHelp: {
    id: 'mpf_videos_exibir_titulo_help',
    defaultMessage:
      'Desligue quando a faixa vier logo abaixo de outra que já tem o cabeçalho.',
  },
  videos: { id: 'mpf_videos_videos', defaultMessage: 'Vídeos' },
  videosHelp: {
    id: 'mpf_videos_videos_help',
    defaultMessage:
      'O primeiro abre no player e os {laterais} seguintes ficam na lateral — {max} no total.',
  },
  video: { id: 'mpf_videos_video', defaultMessage: 'Vídeo' },
  url: { id: 'mpf_videos_url', defaultMessage: 'Endereço no YouTube' },
  urlHelp: {
    id: 'mpf_videos_url_help',
    defaultMessage:
      'Cole o endereço da página do vídeo. A miniatura vem do próprio YouTube.',
  },
  videoTitulo: { id: 'mpf_videos_video_titulo', defaultMessage: 'Título' },
  videoTituloHelp: {
    id: 'mpf_videos_video_titulo_help',
    defaultMessage: 'Aparece abaixo do player e na chamada da lateral.',
  },
  descricao: { id: 'mpf_videos_descricao', defaultMessage: 'Descrição' },
  descricaoHelp: {
    id: 'mpf_videos_descricao_help',
    defaultMessage: 'Só aparece no vídeo que está no player.',
  },
  transcricao: { id: 'mpf_videos_transcricao', defaultMessage: 'Transcrição' },
  transcricaoHelp: {
    id: 'mpf_videos_transcricao_help',
    defaultMessage:
      'Texto do que é dito no vídeo. Aparece abaixo do player, em "Transcrição do vídeo", para quem usa leitor de tela ou não pode ouvir o áudio (e-MAG).',
  },
});

/**
 * Campos de um vídeo da faixa.
 *
 * `itemTitle` é o gancho da nossa customização do `ObjectListWidget`: numa
 * lista de seis vídeos, "VÍDEO #4" não diz nada ao editor — o título, sim.
 */
const videoItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.video),
  addMessage: intl.formatMessage(messages.video),
  itemTitle: (video: { titulo?: string }, index: number) =>
    video?.titulo?.trim() ||
    `${intl.formatMessage(messages.video)} #${index + 1}`,
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['url', 'titulo', 'descricao', 'transcricao'],
    },
  ],
  properties: {
    url: {
      title: intl.formatMessage(messages.url),
      description: intl.formatMessage(messages.urlHelp),
      widget: 'url',
    },
    titulo: {
      title: intl.formatMessage(messages.videoTitulo),
      description: intl.formatMessage(messages.videoTituloHelp),
    },
    descricao: {
      title: intl.formatMessage(messages.descricao),
      description: intl.formatMessage(messages.descricaoHelp),
      widget: 'textarea',
    },
    transcricao: {
      title: intl.formatMessage(messages.transcricao),
      description: intl.formatMessage(messages.transcricaoHelp),
      widget: 'textarea',
    },
  },
  required: ['url'],
});

/**
 * Schema do bloco "Vídeos".
 *
 * `activeObject`/`setActiveObject` são o contrato do `ObjectListWidget` do
 * core: o par só entra em modo controlado com os dois presentes, e `-1` (não
 * `undefined`) é o que mantém a lista com todos os itens fechados — ver
 * `Blocks/Destaques/schema.ts`.
 */
export const mpfVideosSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['titulo', 'exibirTitulo', 'videos'],
    },
  ],
  properties: {
    titulo: {
      title: intl.formatMessage(messages.titulo),
      description: intl.formatMessage(messages.tituloHelp, {
        padrao: VIDEOS_TITULO,
      }),
      placeholder: VIDEOS_TITULO,
    },
    exibirTitulo: {
      title: intl.formatMessage(messages.exibirTitulo),
      description: intl.formatMessage(messages.exibirTituloHelp),
      type: 'boolean',
      widget: 'toggle',
      default: true,
    },
    videos: {
      title: intl.formatMessage(messages.videos),
      description: intl.formatMessage(messages.videosHelp, {
        laterais: VIDEOS_LATERAIS_MAX,
        max: VIDEOS_MAX_ITENS,
      }),
      widget: 'object_list',
      schema: videoItemSchema({ intl }),
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfVideosSchema;
